import { Routes } from '@angular/router';
import { anonymousGuard } from '@core/auth/anonymous.guard';
import { authGuard } from '@core/auth/auth.guard';
import { publicOnlyGuard } from '@core/auth/public-only.guard';
import {
  BENEFIT_PASS_LEGACY_ROUTE,
  BENEFIT_PASS_ROUTE,
} from './core/benefit-pass/benefit-pass-url';
import { legacyProductRedirect, resolveLegacyCmsLink } from './core/router/legacy-cms-link';

const benefitPassRoutePath = BENEFIT_PASS_ROUTE.slice(1);
const legacyBenefitPassRoutePath = BENEFIT_PASS_LEGACY_ROUTE.slice(1);

export const APP_ROUTES: Routes = [
  { path: '', pathMatch: 'full', redirectTo: 'inicio' },
  { path: 'cart', pathMatch: 'full', redirectTo: 'checkout' },
  { path: 'in', pathMatch: 'full', redirectTo: 'ingreso' },
  { path: 'intro', pathMatch: 'full', redirectTo: 'inicio' },
  { path: 'finance', pathMatch: 'full', redirectTo: 'financiamiento' },
  { path: 'financy', pathMatch: 'full', redirectTo: 'financiamiento' },
  { path: 'catalogo', pathMatch: 'full', redirectTo: 'tienda' },
  { path: 'catalogo-experience', pathMatch: 'full', redirectTo: 'descuentos' },
  { path: 'catalogo-especial', pathMatch: 'full', redirectTo: 'especiales' },
  { path: 'beneficios', redirectTo: 'descuentos' },
  { path: 'busqueda/product', pathMatch: 'full', redirectTo: 'busqueda' },
  { path: 'busqueda/experience', pathMatch: 'full', redirectTo: 'busqueda' },
  {
    path: 'busqueda/product/:term',
    redirectTo: ({ params }) =>
      resolveLegacyCmsLink(`busqueda/product/${params['term']}`) ?? 'busqueda',
  },
  {
    path: 'busqueda/experience/:term',
    redirectTo: ({ params }) =>
      resolveLegacyCmsLink(`busqueda/experience/${params['term']}`) ?? 'busqueda',
  },
  {
    path: 'tienda/:group/camp/:campaign',
    redirectTo: ({ params }) =>
      resolveLegacyCmsLink(`tienda/${params['group']}/camp/${params['campaign']}`) ?? 'especiales',
  },
  {
    path: 'tienda/:group/categoria/:category/marca/:brand/producto/:product',
    redirectTo: ({ params }) =>
      resolveLegacyCmsLink(
        `tienda/${params['group']}/categoria/${params['category']}/marca/${params['brand']}/producto/${params['product']}`,
      ) ?? 'productos',
  },
  {
    path: 'catalogo-especial/:group',
    redirectTo: ({ params }) =>
      resolveLegacyCmsLink(`catalogo-especial/${params['group']}`) ?? 'especiales',
  },
  {
    path: 'beneficios/:group/categoria/:category/empresa/:company/beneficio/:benefit',
    redirectTo: ({ params }) =>
      resolveLegacyCmsLink(
        `beneficios/${params['group']}/categoria/${params['category']}/empresa/${params['company']}/beneficio/${params['benefit']}`,
      ) ?? 'descuentos',
  },
  {
    path: 'informacion/preguntas_frecuentes',
    pathMatch: 'full',
    redirectTo: 'informacion/preguntas-frecuentes',
  },
  {
    path: 'informacion/terminos_y_condiciones',
    pathMatch: 'full',
    redirectTo: 'informacion/terminos-y-condiciones',
  },
  {
    path: 'informacion/politicas_de_privacidad',
    pathMatch: 'full',
    redirectTo: 'informacion/politicas-de-privacidad',
  },
  {
    path: 'informacion/libro_de_reclamaciones',
    pathMatch: 'full',
    redirectTo: 'informacion/libro-de-reclamaciones',
  },
  {
    path: 'principal/experience/:group/:category/empresa/:company/:benefit',
    redirectTo: 'descuentos/comercio/:company/descuento/:benefit',
  },
  {
    path: 'principal/experience/:group/:category/empresa/:company',
    redirectTo: 'descuentos/comercio/:company',
  },
  {
    path: 'principal/experience/:group/:category',
    redirectTo: 'descuentos/:category',
  },
  {
    path: 'principal/experience/:group',
    redirectTo: 'descuentos/:group',
  },
  {
    path: 'principal/product/:group/:category/marca/:brand/producto/:product',
    redirectTo: ({ params }) => legacyProductRedirect(params),
  },
  {
    path: 'principal/product/:group/:category/marca/:brand',
    redirectTo: ({ params }) => legacyProductRedirect(params),
  },
  {
    path: 'principal/product/:group/:category',
    redirectTo: ({ params }) => legacyProductRedirect(params),
  },
  {
    path: 'principal/product/:group',
    redirectTo: ({ params }) => legacyProductRedirect(params),
  },
  {
    path: '',
    loadChildren: () =>
      import('@features/preview/preview.routes').then((m) => m.PUBLIC_PREVIEW_ROUTES),
  },
  {
    path: 'informacion',
    canMatch: [publicOnlyGuard],
    loadChildren: () => import('@features/info/info.routes').then((m) => m.INFO_ROUTES),
  },
  {
    path: 'ingreso',
    canMatch: [anonymousGuard],
    loadComponent: () =>
      import('@layouts/auth-layout/auth-layout.component').then((m) => m.AuthLayoutComponent),
    loadChildren: () => import('@features/auth/auth.routes').then((m) => m.AUTH_ROUTES),
  },
  {
    path: '',
    canMatch: [authGuard],
    loadComponent: () =>
      import('@layouts/app-shell/app-shell.component').then((m) => m.AppShellComponent),
    children: [
      {
        path: 'inicio',
        loadChildren: () => import('@features/home/home.routes').then((m) => m.HOME_ROUTES),
        data: { preload: true },
      },
      {
        path: 'productos',
        loadComponent: () =>
          import('@features/tienda/product-catalog/product-catalog.page').then(
            (m) => m.ProductCatalogPage,
          ),
        data: { title: 'Productos', feature: 'productos/catalogo', preload: true, reuse: true },
      },
      {
        path: 'productos/:group',
        loadComponent: () =>
          import('@features/tienda/product-catalog/product-catalog.page').then(
            (m) => m.ProductCatalogPage,
          ),
        data: { title: 'Productos', feature: 'productos/grupo', reuse: true },
      },
      {
        path: 'productos/:group/:category',
        loadComponent: () =>
          import('@features/tienda/product-catalog/product-catalog.page').then(
            (m) => m.ProductCatalogPage,
          ),
        data: { title: 'Productos', feature: 'productos/categoria', reuse: true },
      },
      {
        path: 'tienda',
        loadChildren: () => import('@features/tienda/tienda.routes').then((m) => m.TIENDA_ROUTES),
        data: { preload: true },
      },
      {
        path: 'experiencias',
        loadChildren: () =>
          import('@features/experiencias/experiencias.routes').then((m) => m.EXPERIENCIAS_ROUTES),
        data: { preload: true },
      },
      {
        path: 'especiales',
        loadChildren: () =>
          import('@features/especiales/especiales.routes').then((m) => m.ESPECIALES_ROUTES),
        data: { preload: true },
      },
      {
        path: benefitPassRoutePath,
        loadChildren: () =>
          import('@features/benefit-pass/benefit-pass.routes').then((m) => m.BENEFIT_PASS_ROUTES),
        data: { preload: true },
      },
      { path: legacyBenefitPassRoutePath, pathMatch: 'full', redirectTo: benefitPassRoutePath },
      {
        path: `${legacyBenefitPassRoutePath}/:slug`,
        redirectTo: `${benefitPassRoutePath}/:slug`,
      },
      {
        path: 'descuentos',
        loadChildren: () =>
          import('@features/descuentos/descuentos.routes').then((m) => m.DESCUENTOS_ROUTES),
        data: { preload: true },
      },
      {
        path: 'busqueda',
        loadChildren: () => import('@features/search/search.routes').then((m) => m.SEARCH_ROUTES),
        data: { preload: true },
      },
      {
        path: 'checkout',
        loadChildren: () =>
          import('@features/checkout/checkout.routes').then((m) => m.CHECKOUT_ROUTES),
      },
      {
        path: 'financiamiento',
        loadChildren: () =>
          import('@features/financing/financing.routes').then((m) => m.FINANCING_ROUTES),
      },
      {
        path: 'blog',
        loadChildren: () => import('@features/blog/blog.routes').then((m) => m.BLOG_ROUTES),
      },
      {
        path: 'membresias',
        loadChildren: () =>
          import('@features/membresias/membresias.routes').then((m) => m.MEMBRESIAS_ROUTES),
      },
      {
        path: 'mi-cuenta',
        loadChildren: () => import('@features/cuenta/cuenta.routes').then((m) => m.CUENTA_ROUTES),
      },
      {
        path: 'informacion',
        loadChildren: () => import('@features/info/info.routes').then((m) => m.INFO_ROUTES),
      },
    ],
  },
  {
    path: 'upgrade',
    pathMatch: 'full',
    loadComponent: () => import('@features/upgrade/upgrade.page').then((m) => m.UpgradePage),
    data: { title: 'Upgrade' },
  },
  {
    path: 'no-encontrado',
    loadComponent: () => import('@features/not-found/not-found.page').then((m) => m.NotFoundPage),
  },
  { path: '**', redirectTo: 'no-encontrado' },
];
